import { useCallback, useContext, useEffect, useMemo, useState } from "react";

import { UsersApiHelper } from "api/ApiHelper";
import { AppContext } from "App";
import { useSnackbar } from "notistack";

import { InsuranceService } from "./InsuranceService";

// ponytail: loads all active scenarios' full premium calc up front, same cost
// InsuranceSummaryWorkspace already pays on /insurance — fine at today's scale;
// upgrade path: cheap list-only pass first, premium calc on practice pick.
export function useProducerInsurancePractices(cropYearOverride) {
  const { globalState } = useContext(AppContext);
  const { enqueueSnackbar } = useSnackbar();
  const [isLoading, setIsLoading] = useState(true);
  const [scenarios, setScenarios] = useState([]);

  const cropYear = cropYearOverride ?? globalState?.crop_year;

  const handleError = useCallback(
    (err) => {
      console.warn(err);
      enqueueSnackbar(err.detail || err.message || JSON.stringify(err), { preventDuplicate: true, variant: "error" });
    },
    [enqueueSnackbar]
  );

  const loadPractices = useCallback(async () => {
    if (!cropYear || !globalState?.producer_token) {
      setScenarios([]);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      const producerFilter = {
        producer_token: globalState.producer_token,
        crop_year: cropYear,
        is_active: true,
      };
      const insuranceScenarios = (
        await UsersApiHelper.users_selectObject("InsuranceScenario", producerFilter, globalState)
      )?.sort((a, b) => a.name?.localeCompare(b.name));

      if (!insuranceScenarios?.length) {
        setScenarios([]);
        setIsLoading(false);
        return;
      }

      const insuranceService = new InsuranceService({
        insuranceScenarios,
        crop_year: cropYear,
        onError: (err) => console.warn(err),
        apiConfig: globalState,
      });
      const populated = await insuranceService.actions.loadInsuranceScenario();
      if (!populated?.length) {
        setScenarios([]);
        setIsLoading(false);
        return;
      }

      const withPremiums = [];
      for (const scenario of populated) {
        withPremiums.push(await insuranceService.actions.calculatePremiums(scenario));
      }

      withPremiums.forEach((scenario) => {
        UsersApiHelper.updateInsurancePlans({ InsurancePlans: scenario?.totals?.insurancePlans }, globalState);
      });

      setScenarios(withPremiums);
    } catch (err) {
      setScenarios([]);
      handleError(err);
    } finally {
      setIsLoading(false);
    }
  }, [cropYear, globalState, handleError]);

  useEffect(() => {
    loadPractices();
  }, [loadPractices]);

  const practices = useMemo(() => {
    const rows = [];
    for (const scenario of scenarios) {
      for (const bcp of scenario.budgetCountyPractices || []) {
        rows.push({
          insurance_scenario_id: scenario.id,
          insurance_scenario_name: scenario.name,
          state_name: bcp.state_name,
          county_name: bcp.county_name,
          commodity_name: bcp.commodity_name,
          type_name: bcp.type_name,
          practice_name: bcp.practice_name,
          budgetCountyPractice: bcp,
          insurancePlans: bcp.insurancePlans || [],
        });
      }
    }
    return rows;
  }, [scenarios]);

  const uniqueSorted = useCallback(
    (items, key) => [...new Set(items.map((p) => p[key]).filter(Boolean))].sort((a, b) => a.localeCompare(b)),
    []
  );

  const stateOptions = useMemo(() => uniqueSorted(practices, "state_name"), [practices, uniqueSorted]);

  const countyOptions = useCallback(
    (stateName) =>
      stateName
        ? uniqueSorted(
            practices.filter((p) => p.state_name === stateName),
            "county_name"
          )
        : [],
    [practices, uniqueSorted]
  );

  const commodityOptions = useCallback(
    (stateName, countyName) =>
      stateName && countyName
        ? uniqueSorted(
            practices.filter((p) => p.state_name === stateName && p.county_name === countyName),
            "commodity_name"
          )
        : [],
    [practices, uniqueSorted]
  );

  const typeOptions = useCallback(
    (stateName, countyName, commodityName) =>
      stateName && countyName && commodityName
        ? uniqueSorted(
            practices.filter(
              (p) => p.state_name === stateName && p.county_name === countyName && p.commodity_name === commodityName
            ),
            "type_name"
          )
        : [],
    [practices, uniqueSorted]
  );

  const practiceOptions = useCallback(
    (stateName, countyName, commodityName, typeName) =>
      stateName && countyName && commodityName && typeName
        ? practices.filter(
            (p) =>
              p.state_name === stateName &&
              p.county_name === countyName &&
              p.commodity_name === commodityName &&
              p.type_name === typeName
          )
        : [],
    [practices]
  );

  const findPractice = useCallback(
    (stateName, countyName, commodityName, typeName, practiceName) =>
      practices.find(
        (p) =>
          p.state_name === stateName &&
          p.county_name === countyName &&
          p.commodity_name === commodityName &&
          p.type_name === typeName &&
          p.practice_name === practiceName
      ) ?? null,
    [practices]
  );

  return {
    isLoading,
    cropYear,
    practices,
    scenarios,
    stateOptions,
    countyOptions,
    commodityOptions,
    typeOptions,
    practiceOptions,
    findPractice,
    reload: loadPractices,
  };
}
